import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { ENVIRONMENT } from '@vendorhub/shared/config';
import { validateResponse } from '../validation/validate-response';
import {
  LoginResponseSchema,
  LogoutAllResponseSchema,
  LogoutResponseSchema,
  MeResponseSchema,
  RefreshResponseSchema,
  RegisterResponseSchema,
  RevokeSessionResponseSchema,
  SendEmailOtpResponseSchema,
  SendPhoneOtpResponseSchema,
  SessionsResponseSchema,
  VerifyEmailOtpResponseSchema,
  VerifyPhoneOtpResponseSchema,
  type LoginResponseData,
  type LogoutAllResponseData,
  type MeResponseData,
  type RefreshResponseData,
  type RegisterResponseData,
  type RevokeSessionResponseData,
  type SendEmailOtpResponseData,
  type SendPhoneOtpResponseData,
  type SessionsResponseData,
  type VerifyEmailOtpResponseData,
  type VerifyPhoneOtpResponseData,
} from '../schemas/identity.schema';
import {
  type LoginRequest,
  type LogoutAllRequest,
  type RefreshRequest,
  type RegisterRequest,
  type SendEmailOtpRequest,
  type SendPhoneOtpRequest,
  type VerifyEmailOtpRequest,
  type VerifyPhoneOtpRequest,
} from '../dtos/identity.dto';

/**
 * Thin HTTP wrapper around `/api/v1/identity`. Every response is validated
 * against its Zod schema; callers receive the `data` payload only.
 * `withCredentials` is enabled globally by `authInterceptor`.
 */
@Injectable({ providedIn: 'root' })
export class IdentityApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${inject(ENVIRONMENT).apiBaseUrl}/api/v1/identity`;

  register(req: RegisterRequest): Observable<RegisterResponseData> {
    return this.http.post(this.url('/register'), req).pipe(
      validateResponse(RegisterResponseSchema),
      map((r) => r.data),
    );
  }

  login(req: LoginRequest): Observable<LoginResponseData> {
    return this.http.post(this.url('/login'), req).pipe(
      validateResponse(LoginResponseSchema),
      map((r) => r.data),
    );
  }

  sendEmailOtp(req: SendEmailOtpRequest): Observable<SendEmailOtpResponseData> {
    return this.http.post(this.url('/send-email-otp'), req).pipe(
      validateResponse(SendEmailOtpResponseSchema),
      map((r) => r.data),
    );
  }

  verifyEmailOtp(req: VerifyEmailOtpRequest): Observable<VerifyEmailOtpResponseData> {
    return this.http.post(this.url('/verify-email-otp'), req).pipe(
      validateResponse(VerifyEmailOtpResponseSchema),
      map((r) => r.data),
    );
  }

  sendPhoneOtp(req: SendPhoneOtpRequest): Observable<SendPhoneOtpResponseData> {
    return this.http.post(this.url('/send-phone-otp'), req).pipe(
      validateResponse(SendPhoneOtpResponseSchema),
      map((r) => r.data),
    );
  }

  verifyPhoneOtp(req: VerifyPhoneOtpRequest): Observable<VerifyPhoneOtpResponseData> {
    return this.http.post(this.url('/verify-phone-otp'), req).pipe(
      validateResponse(VerifyPhoneOtpResponseSchema),
      map((r) => r.data),
    );
  }

  refresh(req: RefreshRequest): Observable<RefreshResponseData> {
    return this.http.post(this.url('/refresh'), req).pipe(
      validateResponse(RefreshResponseSchema),
      map((r) => r.data),
    );
  }

  logout(): Observable<void> {
    return this.http.post(this.url('/logout'), {}).pipe(
      validateResponse(LogoutResponseSchema),
      map(() => void 0),
    );
  }

  logoutAll(req: LogoutAllRequest = {}): Observable<LogoutAllResponseData> {
    return this.http.post(this.url('/logout-all'), req).pipe(
      validateResponse(LogoutAllResponseSchema),
      map((r) => r.data),
    );
  }

  me(): Observable<MeResponseData> {
    return this.http.get(this.url('/me')).pipe(
      validateResponse(MeResponseSchema),
      map((r) => r.data),
    );
  }

  sessions(): Observable<SessionsResponseData> {
    return this.http.get(this.url('/sessions')).pipe(
      validateResponse(SessionsResponseSchema),
      map((r) => r.data),
    );
  }

  revokeSession(sessionId: string): Observable<RevokeSessionResponseData> {
    return this.http.delete(this.url(`/sessions/${sessionId}`)).pipe(
      validateResponse(RevokeSessionResponseSchema),
      map((r) => r.data),
    );
  }

  private url(path: string): string {
    return `${this.baseUrl}${path}`;
  }
}
